import * as logger from 'loglevel'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import UserAuthContainer from '../../Containers/UserAuthContainer'
import useAppDisplayDomSetup from '../../Hooks/useAppDisplayDomSetup'
import QrDialog from '../../UI/QrDialog'
import ActionTimeoutDialog from '../../UI/ActionTimeoutDialog'
import AnearHelpDialog from '../../UI/AnearHelpDialog'
import EventConfirmExitDialog from '../../UI/EventConfirmExitDialog'
import { applyFrameDisplay } from '../../Utils/applyAnchorTargets'
import anearApi from '../../Api/AnearApi'
import AppDisplay from './AppDisplay'
import { DefaultContent } from './constants'
import { useActionTimeout } from './useActionTimeout'
import { useChildApps } from './useChildApps'
import { useFrameChannels } from './useFrameChannels'
import { closedConfirmState, useFrameInteractions } from './useFrameInteractions'

const EventFrame = ({ anearEvent, eventState, eventClickHandlers, anearParticipant, bootParticipantHandler }) => {
  const frameContentRef = useRef(null)
  const [html, setHtml] = useState(DefaultContent)
  const [lastId, setLastId] = useState(null)
  // Bump on every display apply (full replace or single-anchor) so useAppDisplayDomSetup
  // re-initializes anear-action, anear-form, anear-timeout in injected content.
  const [displayRevision, setDisplayRevision] = useState(0)
  const [qrOverlayOpen, setQrOverlayOpen] = useState(false)
  const [qrOpenedFromState, setQrOpenedFromState] = useState(null)
  const [helpDialogState, setHelpDialogState] = useState({
    open: false,
    title: '',
    html: '',
    theme: {},
  })
  // Optional confirm dialog for destructive <anear-action prompt="..."> clicks.
  const [actionConfirmState, setActionConfirmState] = useState({
    ...closedConfirmState,
    open: false,
  })
  const lastIdRef = useRef(null)
  const anearParticipantRef = useRef(anearParticipant)
  // Forms whose staged media is still uploading, so a second submit of the same form is
  // dropped while other forms in the display remain submittable.
  const commitInFlightRef = useRef(new Set())
  const userAuth = UserAuthContainer.useContainer()

  const { childAppsContainerRef, activeChildAppsRef } = useChildApps({
    frameContentRef,
    html,
    anearEvent,
    anearParticipant,
    userAuth,
  })

  useEffect(() => {
    lastIdRef.current = lastId
  }, [lastId])

  useEffect(() => {
    anearParticipantRef.current = anearParticipant
  }, [anearParticipant])

  const {
    actionTimeoutState,
    disarmTimeoutWrapper,
    handleTimeoutReset,
    handleTimeoutExit,
  } = useActionTimeout({
    frameContentRef,
    html,
    anearParticipantRef,
    lastIdRef,
    anearParticipant,
    eventClickHandlers,
  })

  // Display handlers. Payload shape: { content } = full replace; { targets } = multi-target.
  // Target patches must not setHtml the frame — that remounts sibling custom elements.
  const displayMessage = useCallback(message => {
    const data = message.data || {}
    const { targets } = data
    setLastId(message.id)

    const root = frameContentRef.current
    if (targets != null && typeof targets === 'object' && !Array.isArray(targets) && !root) {
      logger.debug('[ABR] Targets display skipped: no frame root')
      return
    }

    applyFrameDisplay(root, data, {
      onFullHtml: setHtml,
      onRevision: () => setDisplayRevision(r => r + 1),
      logPrefix: '[ABR]'
    })
  }, [])

  useFrameChannels({
    anearEvent,
    anearParticipant,
    userAuth,
    bootParticipantHandler,
    displayMessage,
    activeChildAppsRef,
  })

  const { onNavClick, onActionClick, onFormSubmit, publishConfirmedAction } = useFrameInteractions({
    eventClickHandlers,
    anearParticipant,
    disarmTimeoutWrapper,
    commitInFlightRef,
    setActionConfirmState,
  })

  const onFileUploadRequest = useCallback(async ({ eventId, file, purpose }) => {
    if (!eventId) throw new Error('Missing event id for upload')
    if (!file) throw new Error('No file selected')
    return anearApi.requestEventMediaUpload(eventId, {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      byteSize: file.size || 0,
      purpose
    })
  }, [])

  const onEventClock = useCallback(async ({ eventId }) => {
    if (!eventId) throw new Error('Missing event id for clock')
    return anearApi.getEventClock(eventId)
  }, [])

  // Generic Workflow Execution trigger/poll for <anear-workflow> elements an
  // AppM renders. App-agnostic: the named workflow + input are validated and
  // authorized server-side (Workflows::Dispatch).
  const onWorkflowStart = useCallback(async ({ workflow, input }) => {
    if (!workflow) throw new Error('Missing workflow name')
    return anearApi.startWorkflow(workflow, input || {})
  }, [])

  const onWorkflowPoll = useCallback(async ({ runId, workflow }) => {
    if (!runId || !workflow) throw new Error('Missing run id or workflow')
    return anearApi.getWorkflowRun(runId, workflow)
  }, [])

  // If the event transitions out of announce while the QR overlay is open
  // (and it was opened during announce), auto-close it so the user sees the game.
  useEffect(() => {
    if (
      qrOverlayOpen &&
      qrOpenedFromState === 'announce' &&
      eventState &&
      eventState !== 'announce'
    ) {
      setQrOverlayOpen(false)
    }
  }, [eventState, qrOverlayOpen, qrOpenedFromState])

  // Central DOM setup
  useAppDisplayDomSetup(
    frameContentRef, {
      userAuthContainer: userAuth,
      anearEvent,
      lastMessageId: lastId,
      displayRevision,
      onNavClick,
      onActionClick,
      onFormSubmit,
      onFileUploadRequest,
      onWorkflowStart,
      onWorkflowPoll,
      onEventClock,
      onPublishAction: (msg) => anearParticipant?.publishAction(msg),
      onQrClick: () => {
        setQrOpenedFromState(eventState || null)
        setQrOverlayOpen(true)
      },
      onHelpOpen: ({ title, html, theme }) => {
        setHelpDialogState({
          open: true,
          title: title || 'Help',
          html: html || '',
          theme: theme || {},
        })
      },
    }
  )

  // Exit/teardown is AppM-driven: terminal display + <anear-nav action="exit"> (or user chrome exit).

  return (
    <>
      <ActionTimeoutDialog
        open={actionTimeoutState.open}
        anearEvent={anearEvent}
        prompt={actionTimeoutState.prompt}
        resetLabel={actionTimeoutState.resetLabel}
        exitLabel={actionTimeoutState.exitLabel}
        modalTimeoutMs={actionTimeoutState.modalTimeoutMs}
        onReset={handleTimeoutReset}
        onExit={handleTimeoutExit}
      />
      <EventConfirmExitDialog
        open={actionConfirmState.open}
        anearEvent={anearEvent}
        prompt={actionConfirmState.prompt}
        stayLabel={actionConfirmState.stayLabel || 'Cancel'}
        exitLabel={actionConfirmState.exitLabel || 'Confirm'}
        onStay={() => setActionConfirmState(closedConfirmState)}
        onExit={() => {
          const { msg, timeoutEl } = actionConfirmState
          setActionConfirmState(closedConfirmState)
          publishConfirmedAction(msg, timeoutEl)
        }}
      />
      <QrDialog
        open={qrOverlayOpen}
        anearEvent={anearEvent}
        onClose={() => setQrOverlayOpen(false)}
      />
      <AnearHelpDialog
        open={helpDialogState.open}
        title={helpDialogState.title}
        html={helpDialogState.html}
        theme={helpDialogState.theme}
        onClose={() =>
          setHelpDialogState({
            open: false,
            title: '',
            html: '',
            theme: {},
          })
        }
      />
      <AppDisplay
        anearEvent={anearEvent}
        html={html}
        frameContentRef={frameContentRef}
        childAppsContainerRef={childAppsContainerRef}
      />
    </>
  )
}

export default EventFrame
